/* import __COLOCATED_TEMPLATE__ from './dependency-warning-modal.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import type IntlService from 'embercom/services/intl';
import type {
  reasonData,
  OutboundMessageDetail,
} from '../channels/email/domains-and-addresses/addresses-table';

interface Args {
  reasons: reasonData[];
  entityName: string;
  updateDependencyCheckStatus: (status: boolean) => void;
  changeStep: (step: string | null, place: string) => void;
}

interface Signature {
  Args: Args;
}

type reasonKey = 'brands' | 'proactive_support';

export default class DependencyWarningModal extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;

  additionalDataArray: Array<string> = [];

  reasonUrlMap = new Map<reasonKey, string>([
    ['brands', '/a/apps/_/settings/workspace/brands'],
    ['proactive_support', '/a/apps/_/outbound/all'],
  ]);

  getDynamicUrl(reason: reasonKey): string {
    return this.reasonUrlMap.get(reason)!;
  }

  reasonIsDefaultWorkspaceAddress(reason: any): boolean {
    return reason.brands && reason.brands.is_default_notification_address;
  }

  getDynamicTranslation(reason: any, type: 'title' | 'description') {
    let key = Object.keys(reason)[0];
    let additionalData;

    if (reason.proactive_support) {
      key = 'proactive-support';
      additionalData = reason.proactive_support!.total;
    } else if (reason.brands) {
      additionalData = reason.brands!.brand_name;
    }

    if (type === 'title') {
      return [
        `new-settings.common.dependency-warning-modal.email-addresses.reasons.${key}-${type}`,
      ];
    } else {
      let blockingDeleteKey =
        reason.proactive_support && additionalData === 1
          ? `new-settings.common.dependency-warning-modal.email-addresses.reasons.${key}-${type}-blocking-delete-singular`
          : `new-settings.common.dependency-warning-modal.email-addresses.reasons.${key}-${type}-blocking-delete`;
      return [
        `new-settings.common.dependency-warning-modal.email-addresses.reasons.${key}-${type}-opener`,
        blockingDeleteKey,
        additionalData,
        `new-settings.common.dependency-warning-modal.email-addresses.reasons.${key}-${type}-closer`,
      ];
    }
  }

  getAdditionalData(index: number) {
    return this.additionalDataArray[index];
  }

  static MAX_VISIBLE_MESSAGES = 10;

  hasOutboundMessages(reason: reasonData): boolean {
    return !!reason.proactive_support?.messages && reason.proactive_support.messages.length > 0;
  }

  getVisibleMessages(reason: reasonData): OutboundMessageDetail[] {
    let messages = reason.proactive_support?.messages ?? [];
    return messages.slice(0, DependencyWarningModal.MAX_VISIBLE_MESSAGES);
  }

  getOverflowCount(reason: reasonData): number {
    let total = reason.proactive_support?.messages?.length ?? 0;
    return Math.max(total - DependencyWarningModal.MAX_VISIBLE_MESSAGES, 0);
  }

  getOutboundEmailUrl(rulesetId: number): string {
    return `/a/apps/_/outbound/email/${rulesetId}`;
  }

  @action
  outboundMessageStateLabel(state: string): string {
    if (state === 'live') {
      return this.intl.t(
        'new-settings.common.dependency-warning-modal.email-addresses.reasons.proactive-support-state-live',
      );
    } else if (state === 'draft') {
      return this.intl.t(
        'new-settings.common.dependency-warning-modal.email-addresses.reasons.proactive-support-state-draft',
      );
    } else if (state === 'paused') {
      return this.intl.t(
        'new-settings.common.dependency-warning-modal.email-addresses.reasons.proactive-support-state-paused',
      );
    } else if (state === 'scheduled') {
      return this.intl.t(
        'new-settings.common.dependency-warning-modal.email-addresses.reasons.proactive-support-state-scheduled',
      );
    }
    return state;
  }

  @action
  openDynamicLink(reason: reasonData) {
    let key = Object.keys(reason)[0];
    let url = this.getDynamicUrl(key as reasonKey);
    safeWindowOpen(url);
  }

  @action
  resetState() {
    this.args.reasons.setObjects([]);
    this.args.updateDependencyCheckStatus(true);
    this.args.changeStep(null, 'dependency-warning-modal');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Common::DependencyWarningModal': typeof DependencyWarningModal;
    'new-settings/common/dependency-warning-modal': typeof DependencyWarningModal;
  }
}
